import React from 'react';
import { FaCode, FaPalette, FaHeadset, FaChartLine, FaArrowRight, FaShieldAlt, FaSearchPlus, FaServer } from 'react-icons/fa';
import useScrollTo from '../hooks/useScrollTo';
import SEOHelmet from './SEO/SEOHelmet';
import { OrganizationSchema, LocalBusinessSchema, WebSiteSchema, ProfessionalServiceSchema } from './SEO/schemas';

const Home = () => {
  const { scrollToSection } = useScrollTo();

  const features = [
    {
      icon: <FaCode className="text-4xl md:text-5xl" />,
      title: 'Digital Development',
      description: 'Enterprise-grade web applications and scalable platforms',
      color: 'from-blue-500 to-primary',
    },
    {
      icon: <FaPalette className="text-4xl md:text-5xl" />,
      title: 'Business Automation',
      description: 'Streamlined workflows and intelligent process automation',
      color: 'from-accent to-orange-600',
    },
    {
      icon: <FaChartLine className="text-4xl md:text-5xl" />,
      title: 'Data & Analytics',
      description: 'Actionable insights through advanced analytics solutions',
      color: 'from-teal to-cyan-600',
    },
    {
      icon: <FaHeadset className="text-4xl md:text-5xl" />,
      title: 'Managed IT Services',
      description: 'Proactive support, infrastructure, and security solutions',
      color: 'from-purple-500 to-pink-500',
    },
  ];

  return (
    <>
      {/* SEO Meta Tags and Structured Data */}
      <SEOHelmet
        title="CitiTekPro | Custom Software Development Minneapolis MN & Digital Solutions"
        description="CitiTekPro is a Minneapolis-based digital agency providing custom software development, web development, mobile app development, SEO services, IT consulting, and business automation for businesses across Minnesota and the Twin Cities."
        keywords="custom software development Minneapolis, web development Minneapolis MN, SEO services Minnesota, digital marketing Twin Cities, IT consulting Minneapolis, business automation Minnesota, AI software development Minnesota, MERN stack development, SaaS development Minneapolis, custom CRM development Minnesota, software company Minneapolis, digital agency Twin Cities"
        canonicalUrl="https://cititekpro.com"
        ogType="website"
      />
      <OrganizationSchema />
      <LocalBusinessSchema />
      <WebSiteSchema />
      <ProfessionalServiceSchema />

      <section
        id="home"
        className="relative pt-16 min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-hidden"
      >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 dark:bg-teal/10 rounded-full filter blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal/10 dark:bg-primary/10 rounded-full filter blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-accent/5 dark:bg-accent/10 rounded-full filter blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {/* Hero Content */}
        <div className="text-center mb-16 md:mb-20 animate-fade-in-up">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in-up">
            <span className="text-gray-900 dark:text-white">Transform Your Business with </span>
            <span className="gradient-text block mt-2">Expert Digital Solutions</span>
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-10 max-w-4xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            CitiTekPro is a Minneapolis-based digital agency specializing in custom software development, responsive web design, business automation, and IT consulting. We partner with businesses across Minnesota and the Twin Cities to build scalable solutions that drive growth and operational excellence.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <button
              onClick={() => scrollToSection('services')}
              className="group relative px-8 py-4 bg-gradient-to-r from-primary to-primary-600 hover:from-primary-600 hover:to-primary-700 dark:from-teal dark:to-teal-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2">
                Explore Services
                <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
              </span>
              <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
            </button>

            <button
              onClick={() => scrollToSection('consultation')}
              className="group px-8 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-primary dark:text-teal font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-primary dark:border-teal"
            >
              <span className="flex items-center gap-2">
                Free Consultation
                <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </button>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative bg-white dark:bg-gray-800 p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 card-hover animate-fade-in-up overflow-hidden"
              style={{ animationDelay: `${0.6 + index * 0.1}s` }}
            >
              {/* Gradient Background on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-2xl`}></div>

              {/* Content */}
              <div className="relative z-10">
                <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${feature.color} text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>

              {/* Decorative Element */}
              <div className="absolute -bottom-2 -right-2 w-20 h-20 bg-gradient-to-br from-primary/5 to-teal/5 dark:from-teal/10 dark:to-primary/10 rounded-full filter blur-xl group-hover:scale-150 transition-transform duration-500"></div>
            </div>
          ))}
        </div>

        {/* Key Strengths Section */}
        <div className="mt-16 md:mt-24 animate-fade-in-up" style={{ animationDelay: '1s' }}>
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Why Leading Businesses Choose CitiTekPro
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Proven expertise delivering measurable results across industries
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: <FaServer />, title: 'Custom Software', subtitle: 'Web Applications', gradient: 'from-blue-500 to-primary' },
              { icon: <FaSearchPlus />, title: 'SEO', subtitle: 'Optimization', gradient: 'from-green-500 to-teal' },
              { icon: <FaShieldAlt />, title: 'Security', subtitle: 'Management', gradient: 'from-purple-500 to-pink-500' },
              { icon: <FaChartLine />, title: 'Analytics', subtitle: 'Insights', gradient: 'from-orange-500 to-accent' },
            ].map((item, index) => (
              <div key={index} className="group text-center p-6 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
                <div className={`inline-flex p-3 rounded-lg bg-gradient-to-br ${item.gradient} text-white mb-3 group-hover:scale-110 transition-transform duration-300`}>
                  <div className="text-2xl">
                    {item.icon}
                  </div>
                </div>
                <div className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">
                  {item.title}
                </div>
                <div className="text-sm md:text-base text-gray-600 dark:text-gray-400">
                  {item.subtitle}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default Home;
